import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, Eye, Share2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Auto-promoted "Featured this week" blog hero tile.
 *
 * Pulls /api/blog/featured which server-side picks the highest-scoring post
 * (views + shares*5) from the last 7 days. Re-evaluates on every load, so the
 * featured slot rotates automatically as traffic shifts — zero manual
 * curation, compounding SEO benefit.
 */
export default function FeaturedBlogHero() {
    const [post, setPost] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/blog/featured`)
            .then((r) => setPost(r.data?.post || null))
            .catch(() => setPost(null))
            .finally(() => setLoaded(true));
    }, []);

    if (!loaded || !post) return null;

    return (
        <section
            className="border-b-2 border-white/20"
            data-testid="featured-blog-hero"
        >
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 items-start">
                    <div className="flex items-start gap-3 lg:flex-col lg:items-start">
                        <div className="inline-flex items-center gap-2 border-2 border-[#FFCC00] px-3 py-2 bg-[#FFCC00]/10">
                            <Flame size={12} className="text-[#FFCC00]" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">
                                FEATURED THIS WEEK
                            </span>
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 hidden lg:block lg:mt-3 max-w-[220px]">
                            AUTO-PROMOTED FROM THE HIGHEST-PERFORMING POST OF THE PAST 7 DAYS.
                        </div>
                    </div>

                    <Link
                        to={`/hpm-blog/${post.slug}`}
                        className="block group border-2 border-white/20 hover:border-[#FFCC00] transition-colors p-8 lg:p-12"
                        data-testid="featured-blog-tile"
                    >
                        {post.category_label && (
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3">
                                / {post.category_label}
                            </div>
                        )}
                        <h2 className="font-anton text-3xl sm:text-5xl uppercase leading-[0.95] mb-5 group-hover:text-[#FFCC00] transition-colors">
                            {post.title}
                        </h2>
                        {post.summary && (
                            <p className="font-sans text-white/70 max-w-3xl leading-relaxed mb-6">
                                {post.summary}
                            </p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-white/50 font-mono text-[10px] uppercase tracking-[0.25em]">
                            <span className="inline-flex items-center gap-1.5">
                                <Eye size={11} /> {post.view_count} VIEWS
                            </span>
                            {post.share_count_total > 0 && (
                                <span className="inline-flex items-center gap-1.5">
                                    <Share2 size={11} /> {post.share_count_total} SHARES
                                </span>
                            )}
                            <span className="inline-flex items-center gap-2 text-white group-hover:text-[#FFCC00] transition-colors">
                                READ THE FULL STORY <ArrowRight size={12} />
                            </span>
                        </div>
                    </Link>
                </div>
            </div>
        </section>
    );
}
